import { useFetchWebsiteHeaderSettings } from '@apps/guest/packages/layout-engine/layouts/hooks/useWebsiteHeaderSettings/useWebsiteHeaderSettings';
import { useFetchCoupleStoryData } from '@apps/guest/packages/layout-engine/widgets/CoupleStory/useFetchCoupleStoryData';
import { useFetchCustomPageData } from '@apps/guest/packages/layout-engine/widgets/Custom/useFetchCustomPageData';
import { useFetchFaqListData } from '@apps/guest/packages/layout-engine/widgets/FaqList/useFetchFaqListData';
import { useFetchRegistryListData } from '@apps/guest/packages/layout-engine/widgets/RegistryList/useFetchRegistryListData';
import { useFetchScheduleListData } from '@apps/guest/packages/layout-engine/widgets/ScheduleList/useFetchScheduleListData';
import { useFetchTidbitsListData } from '@apps/guest/packages/layout-engine/widgets/TidbitsList/useFetchTidbitsListData';
import { useFetchTravelListData } from '@apps/guest/packages/layout-engine/widgets/TravelList/useFetchTravelListData';
import { useGetVips } from '@apps/guest/packages/layout-engine/widgets/WeddingPartyList/useGetVips';
import { useIsGuestSiteSsrEnabled } from '@apps/guest/shared/hooks/useIsGuestSiteSsrEnabled';
import { useFetchWebsitePageListData } from '@shared/hooks/useWebsitePages';

type UsePrefetchSsrQueriesArgs = Readonly<{
  eventHandle: string;
}>;

/**
 * Prefetches SSR-critical queries that would otherwise be blocked by the component tree.
 *
 * Problem: GuestSiteBody.tsx conditionally renders the layout only when `event` is defined
 * (`event && layout ? <LayoutProvider> ... </LayoutProvider> : null`). Queries for page
 * content live inside that layout tree, so `renderToStringWithData` can't discover them
 * until EventWebsiteGuest resolves (Phase 1). This creates a Phase 2 waterfall.
 *
 * Solution: Call these data-fetching hooks here — above the conditional render — so they
 * fire concurrently in Phase 1. Apollo caches the results. When the layout components
 * mount after `event` resolves, they read from Apollo cache instead of making new network
 * requests, eliminating the Phase 2 wall clock cost.
 *
 * These hooks depend on `eventHandle` (available from the URL / SSR preflight) and
 * `featureFlags` (seeded into EventInfoProvider from SSR preflight data). Both are
 * available before BasicEventInfo resolves, so the queries fire immediately.
 *
 * Each hook internally checks `useCurrentPageSlug` and only enables `ssr: true` when
 * the current route matches that page type, so only the relevant page's query blocks
 * the SSR render — the rest fire as non-blocking client-side requests.
 *
 * Accommodations is excluded: it has comprehensive skeleton UI that provides a good
 * loading experience without SSR prefetching.
 *
 * @see doc/improve-guest-site-perf/12-ssr-public-first/PLAN.md — Change 7
 */
export const usePrefetchSsrQueries = (args: UsePrefetchSsrQueriesArgs) => {
  const { eventHandle } = args;

  const isGuestSiteSsrEnabled = useIsGuestSiteSsrEnabled();
  const skip = !isGuestSiteSsrEnabled;

  // Layout-level queries
  useFetchWebsiteHeaderSettings(eventHandle, skip);
  useFetchWebsitePageListData(eventHandle, skip);

  // Page-specific content queries
  useFetchScheduleListData(eventHandle, skip);
  useFetchTravelListData(eventHandle, skip);
  useFetchCoupleStoryData(eventHandle, skip);
  useFetchFaqListData(eventHandle, skip);
  useFetchTidbitsListData(eventHandle, skip);
  useFetchRegistryListData(eventHandle, skip);
  useGetVips({ eventHandle, skip });
  useFetchCustomPageData(eventHandle, skip);
};
